/**
 * Inner zoom for the product gallery.
 *
 * The reference storefront runs Drift in `inner` mode over its main product
 * image: hovering the photograph raises a pane in the same box that shows the
 * full-resolution file at 2x and pans it with the pointer. Nothing here loads
 * that library — the behaviour is short enough to own, and owning it means the
 * pane can read the high-resolution URL Horizon already emits.
 *
 * `product-media.liquid` renders every gallery image with
 * `data_max_resolution` — underscores — which is NOT a `data-*` attribute and
 * so never reaches `dataset`. When it is absent or unreadable the pane rebuilds
 * the CDN URL at the resolution it actually paints instead of reusing the
 * layout-sized file.
 *
 * Scope is deliberately narrow. Thumbnails, product cards and the zoom dialog's
 * own gallery are left alone: a zoom pane on a 120px thumbnail magnifies
 * nothing, and the dialog already has its own.
 */

const MAGNIFY = 2;
const SELECTOR = '.product-media-container .product-media__image';

const canZoom = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class InnerZoom {
  #image;
  #frame;
  #pane;
  #controller = new AbortController();

  constructor(image) {
    this.#image = image;
    // The listener has to sit on the container, not on `.product-media`. Horizon
    // lays a full-cover zoom button over the media as a *sibling* of it, so the
    // pointer never enters `.product-media` at all — it enters the button, and
    // pointerenter does not cross to siblings. Binding one level up is the
    // difference between this working and never firing once.
    this.#frame = image.closest('.product-media-container') ?? image.closest('.product-media');
    if (!this.#frame) return;

    if (getComputedStyle(this.#frame).position === 'static') {
      this.#frame.style.position = 'relative';
    }

    const { signal } = this.#controller;
    this.#frame.addEventListener('pointerenter', this.#open, { signal });
    this.#frame.addEventListener('pointermove', this.#track, { signal });
    this.#frame.addEventListener('pointerleave', this.#close, { signal });
  }

  get #source() {
    // Horizon writes the attribute as `data_max_resolution` — underscores, not
    // hyphens — so it is not a `data-*` attribute at all and
    // `dataset.maxResolution` is permanently undefined. This getter therefore
    // always fell through to `currentSrc`, i.e. the file the *layout* asked
    // for: a 678px frame got the 832px source and the pane upscaled it to
    // 1356. Zooming magnified the same pixels the page already showed.
    const declared =
      this.#image.dataset.maxResolution || this.#image.getAttribute('data_max_resolution');
    if (declared) return declared;

    const src = this.#image.currentSrc || this.#image.src;

    // Otherwise ask Shopify for what the pane actually paints, in device
    // pixels. Shopify caps the request at the uploaded original, so asking for
    // more than exists costs nothing and returns the master.
    try {
      const url = new URL(src, location.href);
      if (!url.searchParams.has('width')) return src;
      const { width } = this.#frame.getBoundingClientRect();
      const needed = Math.ceil(width * MAGNIFY * (window.devicePixelRatio || 1));
      url.searchParams.set('width', String(Math.min(Math.max(needed, 1), 5760)));
      return url.href;
    } catch {
      return src;
    }
  }

  #open = (event) => {
    if (event.pointerType !== 'mouse' || !canZoom()) return;

    // Thumbnails share the container class. Magnifying a 224px crop shows less
    // detail than the main image already does, so they are left alone.
    if (this.#frame.getBoundingClientRect().width < 400) return;

    if (!this.#pane) {
      this.#pane = document.createElement('div');
      this.#pane.className = 'nuvrya-zoom-pane';
      this.#pane.setAttribute('aria-hidden', 'true');
      this.#frame.appendChild(this.#pane);
    }

    const { width, height } = this.#frame.getBoundingClientRect();
    this.#pane.style.backgroundImage = `url("${this.#source}")`;
    this.#pane.style.backgroundSize = `${width * MAGNIFY}px ${height * MAGNIFY}px`;
    this.#track(event);
    // A frame between insertion and the class so the fade actually runs.
    requestAnimationFrame(() => this.#pane?.classList.add('is-open'));
  };

  #track = (event) => {
    if (!this.#pane) return;
    const rect = this.#frame.getBoundingClientRect();
    // At 2x there is exactly one image-width of travel, so the pointer's
    // position across the box maps straight onto background-position.
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.#pane.style.backgroundPosition = `${x.toFixed(2)}% ${y.toFixed(2)}%`;
  };

  #close = () => {
    this.#pane?.classList.remove('is-open');
  };

  destroy() {
    this.#controller.abort();
    this.#pane?.remove();
  }
}

const attached = new WeakSet();

function wireZoom(root = document) {
  if (!canZoom()) return;

  for (const image of root.querySelectorAll?.(SELECTOR) ?? []) {
    if (attached.has(image)) continue;
    // The zoom dialog stacks a second copy of the gallery; it magnifies on its
    // own terms and must not get a pane behind the one it already shows.
    if (image.closest('.dialog-zoomed-gallery')) continue;
    attached.add(image);
    new InnerZoom(image);
  }
}

const start = () => {
  wireZoom();
  // Variant changes swap the gallery's images, and the theme editor re-renders
  // the whole block, so new images have to be picked up as they arrive.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(SELECTOR)) wireZoom(node.parentNode ?? document);
        else wireZoom(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

export { InnerZoom, wireZoom };
